'use client'

import { useState } from 'react'
import { ethers } from 'ethers'
import { useWalletData } from '@/utils'
import { Cancel01Icon } from '@/utils/icons'
import FAUCET_ABI from '@/constants/abi/JSON/MonadexV1Faucet.json'

const FAUCET_ADDRESS = '0x1E4dA2708afF7816aD0481BF427521E5fFD786a4'

const Faucet = (): JSX.Element => {
  const { account, provider } = useWalletData()
  const [showTokens, setShowTokens] = useState(false)
  const [selectedToken, setSelectedToken] = useState('')
  const [claiming, setClaiming] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)

  const tokens = [
    { name: 'PEPE', address: '0xab1fA5cc0a7dB885BC691b60eBeEbDF59354434b' },
    { name: 'Moyaki', address: '0x59897686b2Dd2059b09642797EBeA3d21E6cE2d1' },
    { name: 'Molandak', address: '0xe9f4c0093B4e94800487cad93FBBF7C3729ccf5c' }
  ]

  const handleTokenSelect = (tokenAddress: string): void => {
    setSelectedToken(tokenAddress)
    setErrorMessage(null)
  }

  const checkEligibility = async (contract: ethers.Contract): Promise<boolean> => {
    try {
      const isEligible = await contract.isEligibleToCollect(account, selectedToken)
      return isEligible
    } catch (error) {
      console.error('Eligibility check failed:', error)
      return false
    }
  }

  const handleClaim = async (): Promise<void> => {
    if ((provider == null) || !account) {
      setErrorMessage('Please connect your wallet')
      return
    }
    if (!selectedToken) {
      setErrorMessage('Please select a token')
      return
    }

    setClaiming(true)
    setErrorMessage(null)

    try {
      const signer = provider.getSigner()
      const faucetContract = new ethers.Contract(FAUCET_ADDRESS, FAUCET_ABI, signer)

      // Check eligibility first
      const isEligible = await checkEligibility(faucetContract)
      if (!isEligible) {
        throw new Error('You are not eligible to collect tokens at this time')
      }

      // Simulate the call to catch revert reasons
      try {
        await faucetContract.callStatic.collectTokensFromFaucet(selectedToken, account)
      } catch (simulationError: any) {
        throw new Error(`Simulation failed: ${simulationError.reason || 'Unknown reason'}`)
      }

      // Estimate gas and execute
      const gasEstimate = await faucetContract.estimateGas.collectTokensFromFaucet(selectedToken, account)
      const tx = await faucetContract.collectTokensFromFaucet(selectedToken, account, {
        gasLimit: gasEstimate.mul(120).div(100)
      })

      await tx.wait()
      console.log('Transaction confirmed')


      setSelectedToken('')
      setShowTokens(false)
    } catch (error: any) {
      console.error('Error claiming tokens:', error)
      if (error.code === 'UNPREDICTABLE_GAS_LIMIT') {
        setErrorMessage('Claim failed: Not eligible or faucet lacks funds')
      } 
       else if (error.reason) {
        setErrorMessage(`Claim failed: ${error.reason}`)
      } else {
        setErrorMessage('An error occurred while claiming tokens')
      }
    } finally {
      setClaiming(false)
    }
  }

  return (
    <div className='relative flex flex-col gap-4'>
      <button
        onClick={() => setShowTokens(!showTokens)}
        className='bg-bgColor text-white px-4 py-2 rounded-full hover:bg-primary2 transition-colors duration-200 font-medium'
      >
        Faucet
      </button>



      {showTokens && (
        <div className='fixed inset-0 flex items-center justify-center z-50 bg-black bg-opacity-50'>
          <div
            className='flex flex-col gap-4 p-6 bg-bgColor  border border-primary/20 rounded-xl shadow-xl max-w-sm w-full transform transition-all duration-300 ease-in-out scale-100 hover:scale-105'
          >
            <button
              className='self-end p-1  transition-colors duration-150'
              onClick={() => setShowTokens(false)}
            >
              <Cancel01Icon className='text-white w-6 h-6' />
            </button>

            <div className='flex flex-col gap-3'>
              {tokens.map((token, index) => (
                <button
                  key={index}
                  onClick={() => handleTokenSelect(token.address)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                    selectedToken === token.address
                      ? 'bg-primary text-white shadow-md'
                      : 'bg-primary/5 text-gray-200 hover:bg-primary/50 hover:text-white'
                  }`}
                >
                  {token.name}
                </button>
              ))}
            </div>

            <button
              onClick={handleClaim}
              className='bg-primary text-white px-4 py-2 rounded-lg hover:bg-primary2 transition-colors duration-200 font-medium disabled:opacity-50 disabled:cursor-not-allowed mt-4'
              disabled={selectedToken === '' || claiming || !account}
            >
              {claiming ? 'Claiming...' : 'Claim'}
            </button>

            {errorMessage && (
              <p className='text-red-400 text-sm mt-2 text-center'>{errorMessage}</p>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

export default Faucet
